import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Barcode, ChevronDown, ChevronUp, Cpu, History, Loader2, Pencil, Plus, Search, Trash2, UserRound, Wand2 } from "lucide-react";
import { CodeBarre } from "@/components/scans/CodeBarre";
import { HistoriqueScan } from "@/components/scans/HistoriqueScan";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TourneeSelect } from "@/components/map/ZoneDialog";
import { DefautsEditor, defautsPayload } from "@/components/scans/DefautsEditor";
import { ChauffeurPicker } from "@/components/scans/ChauffeurPicker";
import { StatutBadge, fmtDateHeure, nomChauffeur, useScansData } from "@/components/scans/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { scansSubnav } from "@/lib/subnavs";
import { peut } from "@/lib/permissions";

const NONE = "__none__";
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const MOTIFS_SUPPRESSION = ["Cassée", "Écran cassé", "Ne s'allume plus", "Batterie HS", "Perdue / volée", "Rendue au fournisseur", "Autre"];
const fmtJ = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

/** Matériel (n° de série) d'une scan : plusieurs appareils possibles.
 * « Ajouter scan » : n° de série + date de mise en service (aujourd'hui par
 * défaut). Supprimer un appareil demande un motif ; il passe dans « Ancien
 * matériel », toujours visible. */
function AppareilsScan({ actifs, anciens, onAjouter, onSupprimer }) {
  const [ouvert, setOuvert] = useState(false);
  const [serie, setSerie] = useState("");
  const [jour, setJour] = useState(today());
  const [busy, setBusy] = useState(false);
  const [suppr, setSuppr] = useState(null);   // appareil à supprimer
  const [motif, setMotif] = useState("Cassée");
  const [detail, setDetail] = useState("");
  const [voirAnciens, setVoirAnciens] = useState(false);
  const valider = async () => {
    setBusy(true);
    try {
      if (await onAjouter({ numero_serie: serie.trim(), date_mise_en_service: jour }) !== false) {
        setOuvert(false); setSerie(""); setJour(today());
      }
    } finally { setBusy(false); }
  };
  const supprimer = async () => {
    setBusy(true);
    try {
      if (await onSupprimer(suppr, motif === "Autre" ? detail.trim() : motif) !== false) { setSuppr(null); setMotif("Cassée"); setDetail(""); }
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="scan-appareils">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium flex items-center gap-1.5"><Cpu size={14} /> Matériel ({actifs.length})</p>
        {!ouvert && (
          <Button type="button" size="sm" variant="outline" onClick={() => setOuvert(true)} data-testid="btn-ajouter-appareil">
            <Plus size={13} /> Ajouter scan
          </Button>
        )}
      </div>
      {actifs.length === 0 && <p className="text-xs text-muted-foreground">Aucun appareil enregistré.</p>}
      <ul className="space-y-1" data-testid="appareils-actifs">
        {actifs.map((a) => (
          <li key={a.id || a.numero_serie} className="text-sm">
            <div className="flex items-center gap-2">
              <span className="font-mono font-semibold" data-testid={`appareil-${a.numero_serie}`}>{a.numero_serie}</span>
              <span className="text-xs text-muted-foreground flex-1">en service depuis le {fmtJ(a.date_mise_en_service)}</span>
              {suppr?.numero_serie !== a.numero_serie && (
                <button type="button" onClick={() => setSuppr(a)} className="text-muted-foreground hover:text-destructive p-1" title="Supprimer cet appareil" data-testid={`appareil-suppr-${a.numero_serie}`}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
            {suppr?.numero_serie === a.numero_serie && (
              <div className="mt-1.5 rounded-md bg-destructive/10 p-2 space-y-1.5" data-testid="appareil-suppr-form">
                <Label className="text-xs">Motif de la suppression de {a.numero_serie} *</Label>
                <Select value={motif} onValueChange={setMotif}>
                  <SelectTrigger className="h-9" data-testid="appareil-motif"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOTIFS_SUPPRESSION.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                {motif === "Autre" && <Input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Préciser…" className="h-9" maxLength={200} />}
                <div className="flex justify-end gap-2">
                  <Button type="button" size="sm" variant="ghost" onClick={() => setSuppr(null)}>Annuler</Button>
                  <Button type="button" size="sm" variant="destructive" onClick={supprimer} disabled={busy || (motif === "Autre" && !detail.trim())} data-testid="appareil-suppr-valider">
                    {busy && <Loader2 size={13} className="animate-spin" />} Supprimer
                  </Button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      {ouvert && (
        <div className="rounded-md bg-secondary/60 p-2.5 space-y-2" data-testid="appareil-form">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="app-serie" className="text-xs">N° de série *</Label>
              <Input id="app-serie" value={serie} onChange={(e) => setSerie(e.target.value)} className="font-mono h-9" autoFocus data-testid="appareil-serie"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (serie.trim()) valider(); } }} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="app-date" className="text-xs">Mise en service</Label>
              <Input id="app-date" type="date" value={jour} onChange={(e) => setJour(e.target.value)} className="h-9" data-testid="appareil-date" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOuvert(false)}>Annuler</Button>
            <Button type="button" size="sm" onClick={valider} disabled={!serie.trim() || !jour || busy} data-testid="appareil-valider">
              {busy && <Loader2 size={13} className="animate-spin" />} Ajouter
            </Button>
          </div>
        </div>
      )}
      {anciens.length > 0 && (
        <div className="border-t border-border pt-2">
          <button type="button" onClick={() => setVoirAnciens((v) => !v)} className="text-xs font-medium flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="anciens-ouvrir">
            {voirAnciens ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Ancien matériel ({anciens.length})
          </button>
          {voirAnciens && (
            <ul className="mt-1.5 space-y-1 text-xs" data-testid="anciens-materiel">
              {anciens.map((a) => (
                <li key={a.id} className="flex flex-wrap gap-x-2">
                  <span className="font-mono font-semibold line-through decoration-muted-foreground">{a.numero_serie}</span>
                  <span className="text-muted-foreground">du {fmtJ(a.date_mise_en_service)} au {fmtJ(a.retire_le)}</span>
                  <span className="text-red-600 dark:text-red-400">— {a.motif_retrait}</span>
                  {a.retire_par?.nom && <span className="text-muted-foreground">(par {a.retire_par.nom})</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Codes-barres des tournées : saisis ici, affichés sur la synthèse de la remise
 * (le chauffeur scanne le code de sa tournée). */
function CodesBarresTournees({ tournees, modifiable }) {
  const [codes, setCodes] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [brouillon, setBrouillon] = useState({});
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/scans/codes-barres").then(({ data }) => setCodes(data)).catch(() => setCodes({})); }, []);
  const enregistrer = async (code) => {
    if (!(code in brouillon)) return;   // rien de modifié (ex. sortie du champ après Entrée)
    const v = brouillon[code].trim();
    if (v === (codes?.[code] || "")) return;
    try {
      const { data } = await api.put(`/scans/codes-barres/${code}`, { code_barre: v });
      setCodes(data);
      setBrouillon((b) => { const x = { ...b }; delete x[code]; return x; });
      toast.success(v ? `Code-barres de la tournée ${code} enregistré` : `Code-barres de la tournée ${code} retiré`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const nb = codes ? Object.keys(codes).length : 0;
  const liste = tournees.filter((t) => !q.trim() || `${t.code} ${t.libelle || ""}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="rounded-xl border border-border bg-card mb-4" data-testid="codes-barres-tournees">
      <button type="button" onClick={() => setOuvert((o) => !o)} className="w-full p-4 flex items-center justify-between gap-3 text-left" data-testid="codes-barres-ouvrir">
        <span>
          <span className="font-semibold text-sm flex items-center gap-1.5"><Barcode size={15} /> Codes-barres des tournées</span>
          <span className="block text-xs text-muted-foreground">{nb} / {tournees.length} tournée(s) avec un code-barres (Code 39 : chiffres, lettres, - . $ / + %) — affiché sur la synthèse de la remise de scan.</span>
        </span>
        {ouvert ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {ouvert && (
        <div className="border-t border-border p-3 space-y-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher une tournée…" className="sm:w-64 h-9" />
          <div className="divide-y divide-border">
            {liste.map((t) => {
              const v = brouillon[t.code] ?? codes?.[t.code] ?? "";
              return (
                <div key={t.code} className="py-2 flex flex-wrap items-center gap-3" data-testid={`cb-${t.code}`}>
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: t.couleur }} />
                  <span className="font-mono font-bold w-12">{t.code}</span>
                  <span className="text-sm text-muted-foreground flex-1 min-w-[120px] truncate">{t.libelle}</span>
                  <Input value={v} onChange={(e) => setBrouillon((b) => ({ ...b, [t.code]: e.target.value.toUpperCase().replace(/[^0-9A-Z.$/+%\- ]/g, "") }))}
                    onBlur={() => enregistrer(t.code)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); enregistrer(t.code); } }}
                    placeholder="N° du code-barres" disabled={!modifiable} className="font-mono h-9 w-48" data-testid={`cb-input-${t.code}`} />
                  <span className="w-40 shrink-0">{codes?.[t.code] && <CodeBarre valeur={codes[t.code]} hauteur={30} largeur={1} texte={false} />}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ScanFormDialog({ open, scan, tournees, ssts, onClose, onSaved, onChanged }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [appareils, setAppareils] = useState([]);
  const [enAttente, setEnAttente] = useState([]);   // création : appareils enregistrés après la scan
  useEffect(() => {
    if (!open) return;
    setF(scan ? { ...scan } : { numero: "", tournee_defaut: null, sst: "", date_mise_en_service: today(), actif: true });
    setAppareils(scan?.appareils || []);
    setEnAttente([]);
    setBusy(false);
  }, [open, scan]);
  const ajouterAppareil = async (a) => {
    if (!scan) {
      const serie = a.numero_serie.toUpperCase();
      if (enAttente.some((x) => x.numero_serie === serie)) { toast.error(`${serie} est déjà dans la liste.`); return false; }
      setEnAttente((l) => [...l, { ...a, numero_serie: serie }]);
      setF((x) => ({ ...x, date_mise_en_service: a.date_mise_en_service }));
      return true;
    }
    try {
      const { data } = await api.post(`/scans/${scan.id}/appareils`, a);
      setAppareils(data.appareils);
      setF((x) => ({ ...x, date_mise_en_service: data.date_mise_en_service }));
      toast.success(`Appareil ${a.numero_serie.toUpperCase()} en service sur la scan n° ${scan.numero}`);
      onChanged?.();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const supprimerAppareil = async (a, motif) => {
    if (!scan) { setEnAttente((l) => l.filter((x) => x.numero_serie !== a.numero_serie)); return true; }
    try {
      const { data } = await api.post(`/scans/${scan.id}/appareils/${a.id}/retirer`, { motif });
      setAppareils(data.appareils);
      toast.success(`Appareil ${a.numero_serie} supprimé — visible dans « Ancien matériel »`);
      onChanged?.();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { numero: f.numero, tournee_defaut: f.tournee_defaut || null, sst: f.sst || "", date_mise_en_service: f.date_mise_en_service, actif: !!f.actif };
    try {
      if (scan) await api.put(`/scans/${scan.id}`, body);
      else {
        const { data } = await api.post("/scans", body);
        for (const a of enAttente) await api.post(`/scans/${data.id}/appareils`, a).catch((e) => toast.error(errMsg(e)));
      }
      toast.success(scan ? `Scan n° ${f.numero} modifiée` : `Scan n° ${f.numero} créée`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[92dvh] overflow-y-auto" data-testid="scan-form">
        <form onSubmit={save} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{scan ? `Modifier la scan n° ${scan.numero}` : "Créer une scan"}</DialogTitle>
            <DialogDescription>La tournée par défaut est pré-remplie lors de l'attribution.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="scan-num">N° de scan *</Label>
            <Input id="scan-num" value={f.numero || ""} onChange={(e) => set("numero", e.target.value)} className="font-mono" autoFocus data-testid="scan-numero" />
          </div>
          <div className="space-y-1.5">
            <Label>Tournée par défaut</Label>
            <TourneeSelect
              tournees={tournees}
              value={f.tournee_defaut}
              onChange={(v) => { set("tournee_defaut", v); const t = tournees.find((x) => x.code === v); if (t?.sst && !f.sst) set("sst", t.sst); }}
              allowNone
              noneLabel="— Aucune —"
              testId="scan-tournee"
            />
          </div>
          <div className="space-y-1.5">
            <Label>SST</Label>
            <Select value={f.sst || NONE} onValueChange={(v) => set("sst", v === NONE ? "" : v)}>
              <SelectTrigger data-testid="scan-sst"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— Aucune —</SelectItem>
                {ssts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="scan-date">Date de mise en fonctionnement</Label>
            <Input id="scan-date" type="date" value={f.date_mise_en_service || ""} onChange={(e) => set("date_mise_en_service", e.target.value)} />
          </div>
          <AppareilsScan actifs={scan ? appareils.filter((a) => !a.retire_le) : enAttente} anciens={scan ? appareils.filter((a) => a.retire_le) : []}
            onAjouter={ajouterAppareil} onSupprimer={supprimerAppareil} />
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>
              Active
              <span className="block text-xs text-muted-foreground">Une scan inactive ne peut pas être attribuée.</span>
            </span>
            <Switch checked={!!f.actif} onCheckedChange={(v) => set("actif", v)} data-testid="scan-actif" />
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={!f.numero?.trim() || busy} data-testid="scan-save">{busy && <Loader2 size={14} className="animate-spin" />} {scan ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Pré-attribution automatique d'une scan : tournée + chauffeur utilisés par
 * le bouton « Auto-attribution » de la Remise de scan. */
// Chauffeur proposé pour une tournée : le principal dont c'est la tournée par
// défaut, sinon un chauffeur rattaché à la tournée.
const chauffeurPropose = (chauffeurs, code) => (code
  ? (chauffeurs.find((x) => x.role === "principal" && x.tournee_defaut === code)
    || chauffeurs.find((x) => x.role === "principal" && (x.tournees || []).includes(code))
    || chauffeurs.find((x) => (x.tournees || []).includes(code)))?.id || null
  : null);

function PreattribDialog({ scan, tournees, chauffeurs, onClose, onSaved }) {
  const [actif, setActif] = useState(false);
  const [tournee, setTournee] = useState(null);
  const [chauffeurId, setChauffeurId] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!scan) return;
    const c = scan.preattribution_auto || {};
    setActif(!!c.actif);
    const t = c.tournee || scan.tournee_defaut || null;
    setTournee(t);
    setChauffeurId(c.chauffeur?.id || chauffeurPropose(chauffeurs, t));
    setBusy(false);
  }, [scan]); // eslint-disable-line react-hooks/exhaustive-deps

  const choisirTournee = (v) => {
    setTournee(v);
    // Chauffeur non choisi, ou rattaché à une autre tournée : on propose
    // celui de la nouvelle tournée.
    const actuel = chauffeurs.find((x) => x.id === chauffeurId);
    if (!actuel || !(actuel.tournees || []).includes(v)) {
      const c = chauffeurPropose(chauffeurs, v);
      if (c) setChauffeurId(c);
    }
  };
  const valide = !actif || (tournee && chauffeurId);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/scans/${scan.id}/preattribution-auto`, { actif, tournee: actif ? tournee : null, chauffeur_id: actif ? chauffeurId : null });
      toast.success(actif ? `Pré-attribution activée pour la scan n° ${scan.numero}` : `Pré-attribution désactivée pour la scan n° ${scan.numero}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!scan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md overflow-visible" data-testid="preattrib-dialog">
        <DialogHeader>
          <DialogTitle>Pré-attribution — scan n° {scan?.numero}</DialogTitle>
          <DialogDescription>Activée, la scan est pré-attribuée à ce chauffeur et cette tournée quand on clique sur « Auto-attribution » dans « Remise de scan ».</DialogDescription>
        </DialogHeader>
        <label className="flex items-center justify-between gap-3 text-sm rounded-lg border border-border p-3">
          <span className="font-medium">Pré-attribution {actif ? "activée" : "désactivée"}</span>
          <Switch checked={actif} onCheckedChange={setActif} data-testid="preattrib-actif" />
        </label>
        {actif && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>N° de tournée *</Label>
              <TourneeSelect tournees={tournees} value={tournee} onChange={choisirTournee} testId="preattrib-tournee" />
            </div>
            <div className="space-y-1.5">
              <Label>Chauffeur *</Label>
              <ChauffeurPicker chauffeurs={chauffeurs} tournee={tournee} value={chauffeurId} onChange={setChauffeurId} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={!valide || busy} data-testid="preattrib-valider">{busy && <Loader2 size={14} className="animate-spin" />} Valider</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function HistoriqueDialog({ scan, onClose }) {
  return (
    <Dialog open={!!scan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90dvh] overflow-y-auto" data-testid="historique-dialog">
        <DialogHeader>
          <DialogTitle>Historique — scan n° {scan?.numero}</DialogTitle>
          <DialogDescription>Qui a attribué / réceptionné la scan, à quel chauffeur, à quelle date et heure.</DialogDescription>
        </DialogHeader>
        <HistoriqueScan scanId={scan?.id} />
      </DialogContent>
    </Dialog>
  );
}

export default function ConfigScanPage() {
  const { user } = useAuth();
  const isAdmin = peut(user, "scan_config");   // créer, modifier, pré-attribution, défauts
  const { scans, tournees, chauffeurs, categories, loading, reloadScans } = useScansData();
  const [preattrib, setPreattrib] = useState(null);
  const [form, setForm] = useState(null);
  const [defautsScan, setDefautsScan] = useState(null);
  const [histo, setHisto] = useState(null);
  const [savingDef, setSavingDef] = useState(false);
  const [q, setQ] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  // Remise de scan : contre signature du chauffeur (sinon simple validation)
  const [reglages, setReglages] = useState(null);
  useEffect(() => { api.get("/scans/reglages").then(({ data }) => setReglages(data)).catch(() => {}); }, []);
  const changerSignature = async (v) => {
    try {
      const { data } = await api.put("/scans/reglages", { contre_signature: v });
      setReglages(data);
      toast.success(v ? "Remise de scan contre signature du chauffeur" : "Remise de scan sans signature (simple validation)");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ssts = useMemo(() => [...new Set([...tournees.map((t) => t.sst), ...scans.map((s) => s.sst)].filter(Boolean))].sort(), [tournees, scans]);
  const shown = scans.filter((s) => {
    const t = q.trim().toLowerCase();
    return !t || [s.numero, s.tournee_defaut, s.sst].some((v) => (v || "").toLowerCase().includes(t));
  });

  const saveDefauts = async (list) => {
    setSavingDef(true);
    try {
      await api.put(`/scans/${defautsScan.id}/defauts`, { defauts: defautsPayload(list) });
      toast.success("Éléments défectueux mis à jour");
      setDefautsScan(null);
      reloadScans();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingDef(false); }
  };

  const supprimer = async (s) => {
    try { await api.delete(`/scans/${s.id}`); toast.success(`Scan n° ${s.numero} supprimée`); setConfirmDel(null); reloadScans(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AppShell title="Config scan" subnav={scansSubnav()}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Config scan</h1>
          <p className="text-sm text-muted-foreground">{scans.length} scan(s) · {scans.filter((s) => s.actif === false).length} inactive(s){!isAdmin && " — consultation seule"}</p>
        </div>
        {isAdmin && <Button onClick={() => setForm({ scan: null })} data-testid="btn-creer-scan"><Plus size={15} /> Créer une scan</Button>}
      </div>
      <div className="rounded-xl border border-border bg-card p-4 mb-4 flex items-center justify-between gap-4" data-testid="reglage-signature">
        <div>
          <p className="font-semibold text-sm">Remise contre signature</p>
          <p className="text-xs text-muted-foreground">
            {reglages?.contre_signature
              ? "Activé : à la remise d'une scan, le chauffeur signe à l'écran."
              : "Désactivé : la remise est simplement validée, sans signature du chauffeur."}
          </p>
        </div>
        <Switch checked={!!reglages?.contre_signature} onCheckedChange={changerSignature} disabled={!isAdmin || !reglages} data-testid="switch-contre-signature" />
      </div>

      <CodesBarresTournees tournees={tournees} modifiable={isAdmin} />

      <div className="relative sm:w-72 mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N° de scan, tournée, SST…" className="pl-9" />
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        {loading ? <p className="p-4 text-sm text-muted-foreground">Chargement…</p> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N° scan</TableHead><TableHead>Tournée par défaut</TableHead><TableHead>SST</TableHead>
                <TableHead>N° de série</TableHead><TableHead>Mise en service</TableHead><TableHead>Statut</TableHead><TableHead>Pré-attribution</TableHead><TableHead>Défauts</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((s) => (
                <TableRow key={s.id} data-testid={`config-row-${s.numero}`}>
                  <TableCell className="font-mono font-bold text-base">{s.numero}</TableCell>
                  <TableCell className="font-mono">{s.tournee_defaut || "—"}</TableCell>
                  <TableCell className="text-sm">{s.sst || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {s.appareils_actifs?.length
                      ? <span className="font-mono block leading-tight" data-testid={`serie-${s.numero}`}>{s.appareils_actifs.map((a) => <span key={a.id} className="block">{a.numero_serie}</span>)}</span>
                      : <span className="text-muted-foreground">—</span>}
                    {s.anciens_appareils?.length > 0 && <span className="block text-[11px] text-muted-foreground">{s.anciens_appareils.length} ancien(s)</span>}
                  </TableCell>
                  <TableCell className="text-sm">{s.date_mise_en_service ? new Date(s.date_mise_en_service).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  <TableCell><StatutBadge statut={s.statut} label={s.statut_label} /></TableCell>
                  <TableCell>
                    {s.preattribution_auto?.actif ? (
                      <span className="text-xs leading-tight inline-block" data-testid={`preattrib-info-${s.numero}`}>
                        <span className="font-mono font-semibold">T. {s.preattribution_auto.tournee}</span>
                        <span className="flex items-center gap-1 text-muted-foreground"><UserRound size={11} /> {nomChauffeur(s.preattribution_auto.chauffeur)}</span>
                      </span>
                    ) : <span className="text-xs text-muted-foreground">Désactivée</span>}
                  </TableCell>
                  <TableCell>
                    {s.defauts?.length ? <span className="text-xs font-semibold text-red-600 dark:text-red-400 inline-flex items-center gap-1"><AlertTriangle size={12} /> {s.defauts.length}</span> : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <div className="flex justify-end gap-1">
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => setForm({ scan: s })} title="Modifier" data-testid={`btn-modifier-${s.numero}`}><Pencil size={14} /></Button>}
                      {isAdmin && (
                        <Button size="sm" variant={s.preattribution_auto?.actif ? "secondary" : "outline"} onClick={() => setPreattrib(s)} data-testid={`btn-preattrib-${s.numero}`}>
                          <Wand2 size={13} /> Pré-attribution
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setDefautsScan(s)} data-testid={`btn-defauts-${s.numero}`}><AlertTriangle size={13} /> Éléments défectueux</Button>
                      <Button size="sm" variant="outline" onClick={() => setHisto(s)} data-testid={`btn-historique-${s.numero}`}><History size={13} /> Historique</Button>
                      {isAdmin && (confirmDel === s.id
                        ? <Button size="sm" variant="destructive" onClick={() => supprimer(s)}>Supprimer ?</Button>
                        : <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDel(s.id)} title="Supprimer"><Trash2 size={14} /></Button>)}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!shown.length && <TableRow><TableCell colSpan={8} className="text-sm text-muted-foreground">Aucune scan.{isAdmin && " Cliquez sur « Créer une scan »."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>

      {isAdmin && <ScanFormDialog open={!!form} scan={form?.scan} tournees={tournees} ssts={ssts} onClose={() => setForm(null)} onSaved={() => { setForm(null); reloadScans(); }} onChanged={reloadScans} />}
      <DefautsEditor
        open={!!defautsScan}
        title={`Éléments défectueux — scan n° ${defautsScan?.numero || ""}`}
        value={defautsScan?.defauts || []}
        categories={categories}
        onClose={() => setDefautsScan(null)}
        onValidate={saveDefauts}
        saving={savingDef}
        readOnly={!isAdmin}
      />
      {isAdmin && <PreattribDialog scan={preattrib} tournees={tournees} chauffeurs={chauffeurs} onClose={() => setPreattrib(null)} onSaved={() => { setPreattrib(null); reloadScans(); }} />}
      <HistoriqueDialog scan={histo} onClose={() => setHisto(null)} />
    </AppShell>
  );
}
